(() => {
  'use strict';
  const fail = code => Object.assign(new Error(code), {code});
  const marker = '_letabli_kit';
  function ready(data, now = Date.now()) {
    const gate = data?.checkout;
    const from = Date.parse(gate?.validFrom), until = Date.parse(gate?.validUntil);
    return data?.productionEnabled === true && data.currency === 'EUR' &&
      gate?.enabled === true && gate.revision === data.pricingRevision && !!gate.revision &&
      gate.functionsVerified === true && gate.stockSyncVerified === true &&
      Number.isFinite(from) && Number.isFinite(until) && until > from &&
      until - from <= 21600000 && now >= from && now < until;
  }
  // Other products may sit beside the kit: only the three marked lines are checked.
  // The kit price itself is enforced at checkout by the Shopify rule.
  function verifyCart(cart, products, expected, token) {
    const kit = Array.isArray(cart?.items) ? cart.items.filter(x => x.properties?.[marker] === token) : [];
    if (cart?.currency !== 'EUR' || kit.length !== 3 ||
        new Set(kit.map(p => String(p.variant_id))).size !== 3) throw fail('cart_changed');
    for (const p of products) {
      const line = kit.find(x => String(x.variant_id) === String(p.id));
      if (!line || line.quantity !== 1 || line.properties?.[marker] !== token ||
          line.selling_plan_allocation || line.original_price !== p.price ||
          String(line.product_id) !== String(p.productId).split('/').pop()) throw fail('cart_changed');
    }
    return cart;
  }
  function controller({root, request, makeToken, now = Date.now}) {
    if (!/^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?$/.test(root)) throw fail('cart_route');
    let pending = null, attempt = null;
    const readCart = () => request(root + 'cart.js', {cache: 'no-store'});
    async function add({data, products, expected, refresh}) {
      if (!ready(data, now())) throw fail('cart_unavailable');
      if (products.length !== 3 || new Set(products.map(p => p.productId)).size !== 3 ||
          !products.every(p => /^\d+$/.test(String(p.id)) && Number.isSafeInteger(p.price) && p.price > 0) ||
          !Number.isSafeInteger(expected) || expected <= 0) throw fail('cart_changed');
      const signature = JSON.stringify([products.map(p => [p.id, p.price]), expected, data.revision]);
      const before = await readCart();
      if (attempt) {
        if (attempt.signature !== signature) throw fail('cart_review');
        if (!before.items?.length) throw fail('cart_review');
        return verifyCart(before, products, expected, attempt.token);
      }
      if (before.currency !== 'EUR') throw fail('currency_error');
      if (!Array.isArray(before.items)) throw fail('cart_review');
      if (before.items.some(x => x.properties?.[marker])) throw fail('cart_has_kit');
      const latest = await refresh();
      if (!ready(latest, now()) || latest.pricingRevision !== data.pricingRevision) throw fail('cart_unavailable');
      const live = await Promise.all(products.map(p => request(root + 'products/' + encodeURIComponent(p.handle) + '.js', {cache:'no-store'})));
      if (!live.every((p, i) => p.variants?.some(v => String(v.id) === String(products[i].id) && v.available === true && v.price === products[i].price))) throw fail('changed');
      if (!ready(latest, now())) throw fail('cart_unavailable');
      const token = makeToken();
      if (!/^kc1:[a-zA-Z0-9_-]{8,64}$/.test(token)) throw fail('cart_unavailable');
      attempt = {token, signature};
      try {
        await request(root + 'cart/add.js', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({items:products.map(p => ({id:p.id,quantity:1,properties:{[marker]:token}}))})
        });
      } catch {
        // An HTTP error or timeout may still have added lines. Never repeat POST.
      }
      let after;
      try { after = await readCart(); } catch { throw fail('cart_review'); }
      return verifyCart(after, products, expected, token);
    }
    return {add(args) {
      if (pending) return pending;
      pending = add(args).finally(() => {pending = null;});
      return pending;
    }};
  }
  const api = {ready, verifyCart, controller};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.LetabliKitCart = api;
})();
